import React, { useState } from "react";
import { useAtom } from "jotai";
import { TableRow, TableCell, Stack, Typography, Tooltip } from "@mui/material";
import { allActiveLocationsAtom } from "../../atoms/atoms";
import {
    selectedServiceIdAtom,
    openServiceDialogAtom
} from "../../atoms/servicesTable";
import { AddEditCategoriesDialog } from "../AddEditCategoriesDialog.js";
import CircleIcon from "@mui/icons-material/Circle";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import CurrencyFormat from "react-currency-format";
import { getCurrencySymbol } from "../../utils";
import { MB_SERVICE_CLASSIFICATION_GROUP } from "../../utils/Constants.js";

export const ServiceRow = ({
    serviceData: {
        id,
        active,
        serviceName,
        categoryName,
        CourseSettings,
        type,
        locations,
        bookingBehaviour,
        duration,
        taxExempt,
        price
    }
}) => {
    const theme = useTheme();
    const [, setSelectedServiceId] = useAtom(selectedServiceIdAtom);
    const [allActiveLocations] = useAtom(allActiveLocationsAtom);
    const [openLocationsTooltip, setOpenLocationsTooltip] = useState(false);
    const [openServiceCategoriesDialog, setOpenServiceCategoriesDialog] =
        useState(false);
    const [, setOpenServiceDialog] = useAtom(openServiceDialogAtom);

    const locationIds = locations?.length ? JSON.parse(locations) : [];

    const handleRowClick = (id) => {
        if (!id || openServiceCategoriesDialog) return;
        setSelectedServiceId(id);
        setOpenServiceDialog(true);
    };

    const handleCategoryClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpenServiceCategoriesDialog(true);
    };

    // Extract location names by matching IDs with allActiveLocations
    const getLocationNames = () => {
        const locationNames = locationIds
            .map((id) => {
                const foundLocation = allActiveLocations.find(
                    (loc) => loc.id === id
                );
                return foundLocation ? foundLocation.locationname : "0";
            })
            .sort((a, b) => a.localeCompare(b));

        return locationNames;
    };

    const isActiveLocation = (id) => {
        return allActiveLocations.find((loc) => loc.id === id) ? true : false;
    };

    const handleLocationsTooltipOpen = () => {
        setOpenLocationsTooltip(true);
    };

    const handleLocationsTooltipClose = () => {
        setOpenLocationsTooltip(false);
    };

    return (
        <TableRow key={id} hover onClick={() => handleRowClick(id)}>
            <TableCell
                sx={{
                    width: "1%",
                    textAlign: "center"
                }}
            >
                <Tooltip
                    componentsProps={{
                        tooltip: {
                            sx: {
                                fontWeight: 400,
                                fontSize: "14px !important"
                            }
                        }
                    }}
                    title={active ? "Active" : "Inactive"}
                >
                    <CircleIcon
                        sx={{
                            fontSize: "1.05rem",
                            color: active ? theme.palette.verified : grey[300]
                        }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell
                sx={{
                    width: "34%",
                    minWidth: "200px",
                    whiteSpace: "normal"
                }}
            >
                {serviceName.length > 80 ? (
                    <Tooltip title={serviceName}>
                        <Typography sx={{ display: "inline-block" }}>
                            {`${serviceName.substring(0, 120)}...`}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography>{serviceName}</Typography>
                )}
            </TableCell>
            <TableCell
                sx={{
                    width: "15%",
                    minWidth: "160px",
                    whiteSpace: "normal"
                }}
            >
                <Tooltip title={categoryName.length > 80 ? categoryName : ""}>
                    <Typography
                        variant="link"
                        sx={{
                            display: "inline-block",
                            cursor: "pointer"
                        }}
                        onClick={handleCategoryClick}
                    >
                        {categoryName.length > 80
                            ? `${categoryName.substring(0, 80)}...`
                            : categoryName}
                    </Typography>
                </Tooltip>
            </TableCell>
            {openServiceCategoriesDialog && (
                <AddEditCategoriesDialog
                    open={openServiceCategoriesDialog}
                    onClose={() => setOpenServiceCategoriesDialog(false)}
                    categoryClicked={categoryName}
                />
            )}
            <TableCell
                sx={{
                    width: "5%",
                    whiteSpace: "normal"
                }}
            >
                <Typography>{type}</Typography>
            </TableCell>
            <TableCell
                sx={{
                    textAlign: "center",
                    width: "5%"
                }}
            >
                <Typography
                    sx={{
                        position: "relative",
                        translate: "-14%"
                    }}
                >
                    {CourseSettings?.capacity > 1 ? CourseSettings.capacity : 1}
                </Typography>
            </TableCell>
            {/* <TableCell
                sx={{
                    textAlign: "center",
                    width: "5%"
                }}
            >
                <Typography
                    sx={{
                        position: "relative",
                        translate: "-14%"
                    }}
                >
                    {type === MB_SERVICE_CLASSIFICATION_GROUP
                        ? CourseSettings?.sessions?.length > 1
                            ? CourseSettings.sessions.length
                            : "Indefinitely"
                        : "Ongoing"}
                </Typography>
            </TableCell> */}
            <TableCell
                sx={{
                    textAlign: "center",
                    width: "5%"
                }}
            >
                <Tooltip
                    title={
                        locationIds.length > 0 ? (
                            <Stack>
                                {getLocationNames().map(
                                    (name, index) =>
                                        name !== 0 && (
                                            <Typography key={index}>
                                                {name}
                                            </Typography>
                                        )
                                )}
                            </Stack>
                        ) : (
                            ""
                        )
                    }
                    open={openLocationsTooltip}
                    onOpen={handleLocationsTooltipOpen}
                    onClose={handleLocationsTooltipClose}
                    placement="top"
                >
                    <Typography
                        sx={{
                            position: "relative",
                            translate: "-10%"
                        }}
                        onMouseEnter={handleLocationsTooltipOpen}
                        onMouseLeave={handleLocationsTooltipClose}
                    >
                        {locationIds?.length === 1 &&
                        !isActiveLocation(locationIds[0])
                            ? "0"
                            : locationIds?.length}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell
                sx={{
                    width: "5%",
                    textAlign: "center"
                }}
            >
                <Typography
                    sx={{
                        position: "relative",
                        translate: "-14%"
                    }}
                >
                    {duration}
                </Typography>
            </TableCell>
            <TableCell
                sx={{
                    width: "10%"
                }}
            >
                <Typography>{bookingBehaviour}</Typography>
            </TableCell>
            <TableCell
                sx={{
                    width: "5%"
                }}
            >
                <Typography>{taxExempt} </Typography>
            </TableCell>
            <TableCell
                sx={{
                    textAlign: "right",
                    width: "5%",
                    translate: "-10%"
                }}
            >
                <Typography>
                    <CurrencyFormat
                        value={price}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={getCurrencySymbol()}
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                </Typography>
            </TableCell>
        </TableRow>
    );
};
